import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout/Layout';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
    Package, 
    Clock, 
    CheckCircle, 
    Truck, 
    XCircle,
    ChevronRight,
    ArrowLeft,
    ExternalLink,
    FileText,
    Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const OrdersPage = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);

    const fetchOrders = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data.orders);
        } catch (error) {
            console.error('Fetch orders error:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Download packing slip
    const handleDownloadPackingSlip = async (e, order) => {
        e.stopPropagation();
        setDownloading(`packing-${order.id}`);
        try {
            const response = await axios.get(
                `${API_URL}/orders/${order.id}/packing-slip`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `bon_commande_${order.id.slice(0, 8)}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Bon de commande téléchargé');
        } catch (error) {
            toast.error('Bon de commande non disponible');
        } finally {
            setDownloading(null);
        }
    };

    // Download invoice
    const handleDownloadInvoice = async (e, order) => {
        e.stopPropagation();
        setDownloading(`invoice-${order.id}`);
        try {
            const response = await axios.get(
                `${API_URL}/orders/${order.id}/invoice`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            // Get filename from header
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Facture_${order.pennylane_invoice_number || order.invoice_number || order.id.slice(0,8)}.pdf`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
                if (match && match[1]) filename = match[1];
            }
            
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Facture téléchargée');
        } catch (error) {
            toast.error('Facture non disponible');
        } finally {
            setDownloading(null);
        }
    };

    // Download proforma
    const handleDownloadProforma = (e, order) => {
        e.stopPropagation();
        if (order.pennylane_proforma_pdf_url) {
            window.open(order.pennylane_proforma_pdf_url, '_blank');
            toast.success('Proforma ouverte dans un nouvel onglet');
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'pending':
                return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
            case 'paid':
                return <Badge className="bg-blue-100 text-blue-800"><CheckCircle className="w-3 h-3 mr-1" />Payée</Badge>;
            case 'confirmed':
                return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Confirmée</Badge>;
            case 'preparing':
                return <Badge className="bg-orange-100 text-orange-800"><Package className="w-3 h-3 mr-1" />En préparation</Badge>;
            case 'partially_shipped':
                return <Badge className="bg-cyan-100 text-cyan-800"><Truck className="w-3 h-3 mr-1" />Partiellement expédiée</Badge>;
            case 'shipped':
                return <Badge className="bg-indigo-100 text-indigo-800"><Truck className="w-3 h-3 mr-1" />Expédiée</Badge>;
            case 'delivered':
                return <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" />Livrée</Badge>;
            case 'cancelled':
                return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Annulée</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Layout>
            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                        Mes Commandes
                    </h1>
                    <Button onClick={() => navigate('/catalog')} data-testid="go-catalog">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour au catalogue
                    </Button>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <Card key={i} className="border-slate-200">
                                <CardContent className="p-6">
                                    <div className="animate-pulse space-y-4">
                                        <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                                        <div className="h-6 bg-slate-200 rounded w-1/2"></div>
                                        <div className="h-4 bg-slate-200 rounded w-1/3"></div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-16">
                        <Package className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                        <h2 className="text-xl font-semibold text-slate-900 mb-2">Aucune commande</h2>
                        <p className="text-slate-600 mb-6">Vous n&apos;avez pas encore passé de commande</p>
                        <Button onClick={() => navigate('/catalog')} data-testid="start-shopping">
                            Parcourir le catalogue
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {/* Header row */}
                        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs font-medium text-slate-500 border-b border-slate-200">
                            <div className="col-span-2">N° Commande</div>
                            <div className="col-span-2">Statut</div>
                            <div className="col-span-2 hidden sm:block">Date</div>
                            <div className="col-span-1 text-center">Qté</div>
                            <div className="col-span-1 text-right">Total</div>
                            <div className="col-span-2">N° Suivi</div>
                            <div className="col-span-2 text-center">Documents</div>
                        </div>
                        
                        {orders.map((order) => (
                            <Card 
                                key={order.id} 
                                className="border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer rounded-lg"
                                onClick={() => navigate(`/orders/${order.id}`)}
                                data-testid={`order-${order.id}`}
                            >
                                <CardContent className="py-2.5 px-4">
                                    <div className="grid grid-cols-12 gap-2 items-center">
                                        {/* N° Commande */}
                                        <div className="col-span-2">
                                            <span className="font-mono font-medium text-slate-900 text-sm">
                                                #{order.id.slice(0, 8)}
                                            </span>
                                        </div>
                                        
                                        {/* Statut */}
                                        <div className="col-span-2">
                                            {getStatusBadge(order.status)}
                                        </div>
                                        
                                        {/* Date */}
                                        <div className="col-span-2 hidden sm:block">
                                            <span className="text-xs text-slate-500">
                                                {formatDate(order.created_at)}
                                            </span>
                                        </div>
                                        
                                        {/* Quantité */}
                                        <div className="col-span-1 text-center">
                                            <span className="text-sm text-slate-600">
                                                {order.items.length}
                                            </span>
                                        </div>
                                        
                                        {/* Total */}
                                        <div className="col-span-1 text-right">
                                            <span className="font-semibold text-slate-900 text-sm">
                                                {(() => {
                                                    // Use original_total_ttc if credit was applied, otherwise total_ttc
                                                    const originalTtc = order.original_total_ttc;
                                                    const ttc = order.total_ttc;
                                                    const ht = order.total_ht || 0;
                                                    const shipping = order.shipping_cost || 0;
                                                    const shippingVat = order.shipping_vat || 0;
                                                    
                                                    // Priority: original_total_ttc (full amount before credit)
                                                    if (originalTtc && originalTtc > 0) return originalTtc.toFixed(2);
                                                    if (ttc && ttc > 0) return ttc.toFixed(2);
                                                    
                                                    // Calculate full total from items + shipping
                                                    const itemsTotal = order.items?.reduce((sum, item) => sum + (item.price || 0), 0) || 0;
                                                    const itemsVat = order.items?.reduce((sum, item) => {
                                                        if (item.vat_regime === 'standard') return sum + (item.price * 0.20);
                                                        return sum;
                                                    }, 0) || 0;
                                                    return (itemsTotal + itemsVat + shipping + shippingVat).toFixed(2);
                                                })()} €
                                            </span>
                                        </div>
                                        
                                        {/* N° Suivi */}
                                        <div className="col-span-2">
                                            {(order.shipments?.length || 0) > 0 ? (
                                                <div className="space-y-1">
                                                    {order.shipments.slice(0, 2).map((shipment, idx) => (
                                                        <div key={idx} className="flex items-center gap-1">
                                                            <span className="text-xs text-slate-500">#{idx + 1}</span>
                                                            {shipment.tracking_number ? (
                                                                <>
                                                                    <span className="font-mono text-xs text-slate-600 truncate max-w-[80px]">
                                                                        {shipment.tracking_number}
                                                                    </span>
                                                                    {shipment.tracking_url && (
                                                                        <a
                                                                            href={shipment.tracking_url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            className="text-indigo-600 hover:text-indigo-800"
                                                                            title="Suivre le colis"
                                                                        >
                                                                            <ExternalLink className="w-3 h-3" />
                                                                        </a>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="text-xs text-slate-400">Sans suivi</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {order.shipments.length > 2 && (
                                                        <span className="text-xs text-slate-400">+{order.shipments.length - 2} autre(s)</span>
                                                    )}
                                                </div>
                                            ) : order.tracking_number ? (
                                                <div className="flex items-center gap-1">
                                                    <span className="font-mono text-xs text-slate-600 truncate">
                                                        {order.tracking_number}
                                                    </span>
                                                    {order.tracking_url && (
                                                        <a
                                                            href={order.tracking_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="text-indigo-600 hover:text-indigo-800"
                                                            title="Suivre le colis"
                                                        >
                                                            <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-400">—</span>
                                            )}
                                        </div>
                                        
                                        {/* Documents */}
                                        <div className="col-span-2 flex justify-center gap-2">
                                            {/* Bon de commande - disponible après paiement */}
                                            {['paid', 'confirmed', 'preparing', 'partially_shipped', 'shipped', 'delivered'].includes(order.status) && (
                                                <button
                                                    onClick={(e) => handleDownloadPackingSlip(e, order)}
                                                    disabled={downloading === `packing-${order.id}`}
                                                    className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                                    title="Télécharger le bon de commande"
                                                >
                                                    <FileText className="w-4 h-4" />
                                                </button>
                                            )}
                                            
                                            {/* Proforma - disponible si générée */}
                                            {order.pennylane_proforma_pdf_url && (
                                                <button
                                                    onClick={(e) => handleDownloadProforma(e, order)}
                                                    className="p-1.5 text-amber-500 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                                                    title={`Proforma ${order.pennylane_proforma_number || ''}`}
                                                >
                                                    <FileText className="w-4 h-4" />
                                                </button>
                                            )}
                                            
                                            {/* Facture - disponible après expédition */}
                                            {(order.pennylane_invoice_number || order.invoice_number) && (
                                                <button
                                                    onClick={(e) => handleDownloadInvoice(e, order)}
                                                    disabled={downloading === `invoice-${order.id}`}
                                                    className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                                                    title={`Télécharger la facture ${order.pennylane_invoice_number || order.invoice_number}`}
                                                >
                                                    <Download className="w-4 h-4" />
                                                </button>
                                            )}
                                            
                                            {/* Si pas de documents, afficher un chevron */}
                                            {!['paid', 'confirmed', 'preparing', 'partially_shipped', 'shipped', 'delivered'].includes(order.status) && 
                                             !order.pennylane_invoice_number && !order.invoice_number && !order.pennylane_proforma_pdf_url && (
                                                <ChevronRight className="w-4 h-4 text-slate-400" />
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
            <Footer />
        </Layout>
    );
};

export default OrdersPage;
